import { useState, type ElementType } from "react";
import {
  Box,
  Stack,
  Typography,
  ButtonBase,
  Chip,
  Divider
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import PauseRounded from "@mui/icons-material/PauseRounded";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import DeleteForeverRounded from "@mui/icons-material/DeleteForeverRounded";

import type { Bot } from "@/data/mock-data";
import { formatUSD } from "@/lib/formatters";
import { useSwipeReveal } from "@/hooks/useSwipeReveal";
import { ItemActionSheet } from "@/components/ItemActionSheet";
import type { ItemAction } from "@/components/ItemActionSheet";

// ---- Public component -----------------------------------------------------

interface BotsSimpleViewProps {
  /** Already filtered + sorted by the caller. */
  bots: Bot[];
  /**
   * Click handler — opens the bot detail drawer (BotsPanel hosts the
   * drawer; this view just signals which row was tapped). Lifted so
   * callers can decide whether to navigate, open a drawer, or short-
   * circuit in tests.
   */
  onBotClick?: (id: string) => void;
  /**
   * Optional per-bot weekly $/% delta (Mon-Fri so far). Used as the
   * fallback when today's change isn't available (e.g. weekends, or
   * the bot snapshot didn't yet capture today's movement). Keyed by
   * bot id; missing entries fall through to "—". Same code path for
   * demo and real sessions — caller decides whether to populate.
   */
  weeklyDeltas?: Record<string, { fraction: number; delta: number }>;
}

/**
 * Mobile bot list — minimal, post-Apr-2026 strip-down.
 *
 * Design brief (Sergey 2026-04-26 annotated screenshot):
 *  • No top-of-list portfolio summary card — duplicates the hero card on
 *    AccountsPageV2 above the tab strip and adds vertical noise.
 *  • Each row: bot identity + the three numbers worth surfacing on a
 *    phone-sized list — `today` change, total return $, and TWR %.
 *    Everything else (alloc, APY, Sharpe, max DD, etc.) lives in the
 *    bot detail drawer that opens on tap.
 *  • Sparkline kept — the equity-curve shape is the single most useful
 *    "at a glance" signal, and dropping it would make every row look
 *    interchangeable.
 *  • Flat rows separated by a single Divider — no per-row card chrome.
 */
export function BotsSimpleView({
  bots,
  onBotClick,
  weeklyDeltas
}: BotsSimpleViewProps) {
  const [menuBot, setMenuBot] = useState<Bot | null>(null);

  if (bots.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          color: "text.secondary"
        }}
      >
        <Typography variant="body2">No bots match your filters.</Typography>
      </Box>
    );
  }

  // Action-sheet contents for a given bot. Pause/Resume swaps based on
  // the bot's current status. All handlers are no-op placeholders — the
  // sheet just closes on select, matching the current UX spec.
  const buildBotActions = (b: Bot): ItemAction[] => {
    const paused = b.status === "PAUSED";
    return [
      {
        id: "view",
        label: "View details",
        icon: <VisibilityRounded fontSize="small" />,
        onSelect: () => onBotClick?.(b.id)
      },
      {
        id: "add_money",
        label: "Add money",
        icon: <AddRounded fontSize="small" />,
        onSelect: () => {}
      },
      {
        id: "withdraw",
        label: "Withdraw / sell",
        icon: <RemoveRounded fontSize="small" />,
        onSelect: () => {}
      },
      {
        id: "pause_resume",
        label: paused ? "Resume" : "Pause",
        icon: paused ? (
          <PlayArrowRounded fontSize="small" />
        ) : (
          <PauseRounded fontSize="small" />
        ),
        onSelect: () => {}
      },
      {
        id: "liquidate",
        label: "Liquidate",
        description: "Close all positions and deactivate",
        icon: <DeleteForeverRounded fontSize="small" />,
        onSelect: () => {},
        destructive: true
      }
    ];
  };

  return (
    <>
      <Box sx={{ pt: 1 }}>
        {bots.map((b, i) => (
          <Box key={b.id}>
            {i > 0 && <Divider />}
            <BotCardSimple
              bot={b}
              weeklyDelta={weeklyDeltas?.[b.id]}
              onClick={onBotClick ? () => onBotClick(b.id) : undefined}
              onSwipeOpen={setMenuBot}
            />
          </Box>
        ))}
      </Box>

      <ItemActionSheet
        open={menuBot !== null}
        onClose={() => setMenuBot(null)}
        title={menuBot ? menuBot.name : ""}
        subtitle={
          menuBot
            ? `${formatUSDSmart(menuBot.currentValue)} · ${menuBot.status === "PAUSED" ? "Paused" : "Active"}`
            : undefined
        }
        actions={menuBot ? buildBotActions(menuBot) : []}
      />
    </>
  );
}

// ---- Bot row --------------------------------------------------------------

function BotCardSimple({
  bot,
  weeklyDelta,
  onClick,
  onSwipeOpen
}: {
  bot: Bot;
  weeklyDelta?: { fraction: number; delta: number };
  onClick?: () => void;
  onSwipeOpen?: (bot: Bot) => void;
}) {
  const totalPositive = bot.allTimeGainPct >= 0;
  // Universal fallback chain — same logic for demo and real sessions
  // (Sergey 2026-04-27: UI must look identical regardless of where
  // the bot data came from):
  //
  //   1. dayChangePct non-zero & finite → "Today"
  //   2. weeklyDelta provided          → "This week"
  //   3. neither                        → "—"
  //
  // Demo bots in the mock fixture have non-zero dayChangePct so they
  // land on "Today" naturally. Real bots with a fresh /bots/{id}
  // overlay also land on "Today" (overlay populates dayChangePct from
  // states[]); on weekends or before the overlay loads, real bots
  // fall through to "This week" or "—".
  const hasToday =
    Number.isFinite(bot.dayChangePct) && bot.dayChangePct !== 0;
  const periodMode: "today" | "week" | "none" = hasToday
    ? "today"
    : weeklyDelta
      ? "week"
      : "none";
  const periodLabel = periodMode === "week" ? "This week" : "Today";
  const periodFraction =
    periodMode === "today"
      ? bot.dayChangePct
      : periodMode === "week"
        ? weeklyDelta!.fraction
        : 0;
  const periodDelta =
    periodMode === "today"
      ? bot.dayChangePct * bot.currentValue
      : periodMode === "week"
        ? weeklyDelta!.delta
        : 0;
  const periodPositive = periodFraction >= 0;

  // Use ElementType so TS can resolve the JSX for either ButtonBase
  // (when the row is clickable) or plain Box (when it isn't) —
  // `typeof ButtonBase` alone trips TS2604 because of MUI's polymorphic
  // component signature.
  const Wrapper: ElementType = onClick ? ButtonBase : Box;

  // Swipe-to-reveal the bot action sheet. The hook returns touch
  // handlers, a translateX value for drag-feedback, and a click
  // suppressor so the tap that ends a qualifying swipe doesn't also
  // trigger the row's tap-to-open onClick.
  const { handlers, translateX, consumeClickSuppression } = useSwipeReveal({
    onSwipe: () => onSwipeOpen?.(bot)
  });

  const handleClick = onClick
    ? () => {
        if (consumeClickSuppression()) return;
        onClick();
      }
    : undefined;

  return (
    <Wrapper
      {...(handleClick ? { onClick: handleClick } : {})}
      {...handlers}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        // Flat row — no border, no radius, no background panel.
        // Vertical padding tuned to MD3 two-line list spec without
        // burning extra viewport when the row carries no sparkline
        // (real bots): py 1.25 ≈ 10dp top + 10dp bottom, plus the
        // internal numbers row's own 12dp spacing → ~32dp tap target.
        // Sergey 2026-04-26: previous py:2 made the rows feel like
        // they had blank space stacked between them.
        py: 1.25,
        px: 0,
        touchAction: "pan-y",
        transform: `translateX(${translateX}px)`,
        transition:
          translateX === 0
            ? "background-color 120ms ease, transform 180ms ease"
            : "background-color 120ms ease",
        "&:hover": onClick
          ? {
              bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.04)
            }
          : undefined
      }}
    >
      {/* Header row: icon + name + portfolio value, all on a single
       *  vertically-centered baseline. Tickers caption moved to the
       *  drawer's "Universe" row — keeping this line single-height
       *  lets the icon sit on the text baseline like Robinhood's row
       *  treatment (Sergey 2026-04-26). Paused chip slips in between
       *  name and value when applicable. */}
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <BotIcon status={bot.status} />
        <Typography
          variant="body1"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 700,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {bot.name}
        </Typography>
        {bot.archived ? (
          <Chip
            label={
              bot.archivedAt
                ? `Archived · ${formatArchivedDate(bot.archivedAt)}`
                : "Archived"
            }
            size="small"
            sx={(t) => ({
              bgcolor: alpha(t.palette.text.secondary, 0.15),
              color: t.palette.text.secondary,
              fontWeight: 600,
              height: 22,
              "& .MuiChip-label": { px: 1.25, fontSize: 11 }
            })}
          />
        ) : bot.status === "PAUSED" ? (
          <Chip
            label="Paused"
            size="small"
            sx={(t) => ({
              bgcolor: alpha(t.palette.warning.main, 0.15),
              color: t.palette.warning.main,
              fontWeight: 600,
              height: 22,
              "& .MuiChip-label": { px: 1.25, fontSize: 11 }
            })}
          />
        ) : null}
        <Typography
          variant="body1"
          sx={{
            flexShrink: 0,
            fontWeight: 700,
            fontFeatureSettings: '"tnum" 1',
            lineHeight: 1.2,
            whiteSpace: "nowrap"
          }}
        >
          {formatUSDSmart(bot.currentValue)}
        </Typography>
      </Stack>

      {/* Numbers row — kept post-strip-down (Sergey 2026-04-26):
       *  Today on the left, Total return $ + TWR% on the right. Layout
       *  uses a 1fr 1fr grid (NOT space-between) so the two columns
       *  fill the row evenly — the previous space-between left a big
       *  empty channel down the middle on wide phones. Anything else
       *  (Alloc, APY, Sharpe, Max DD, etc.) lives in the bot detail
       *  drawer behind a tap on the row. */}
      <Box
        sx={{
          mt: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: 2,
          alignItems: "end",
          fontFeatureSettings: '"tnum" 1'
        }}
      >
        {/* Today / This week — Robinhood-style: ▲/▼ triangle prefix in
         *  green/red, dollar amount, then ( pct% ) inline. The
         *  triangle conveys direction so we drop the +/− prefix on
         *  the numbers (Sergey 2026-04-26). */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              display: "block",
              lineHeight: 1.2,
              mb: 0.25
            }}
          >
            {periodLabel}
          </Typography>
          {periodMode === "none" ? (
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontWeight: 700,
                fontFeatureSettings: '"tnum" 1',
                lineHeight: 1.2
              }}
            >
              —
            </Typography>
          ) : (
            <DeltaValueRH
              positive={periodPositive}
              dollar={periodDelta}
              fraction={periodFraction}
            />
          )}
        </Box>

        {/* Total return — same Robinhood-style format. No caption per
         *  earlier polish; the row's right edge alignment + green/red
         *  triangle is enough context. */}
        <Box sx={{ textAlign: "right", minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{
              color: "transparent",
              display: "block",
              lineHeight: 1.2,
              mb: 0.25,
              userSelect: "none"
            }}
          >
            {/* Spacer matched to left column's "This week" label so
             *  the dollar baselines line up. Same body1 size as the
             *  label across this view (Sergey 2026-04-26: typography
             *  must match Buying Power baseline). */}
            &nbsp;
          </Typography>
          <DeltaValueRH
            align="right"
            positive={totalPositive}
            dollar={bot.allTimeGain}
            fraction={bot.allTimeGainPct}
          />
        </Box>
      </Box>

      {/* Sparkline removed from the simple-view row (Sergey 2026-04-27).
       *  The shape is still useful at-a-glance, but the chart pulls
       *  the eye away from the headline numbers and adds vertical
       *  weight that fights the dense list. The drawer keeps a
       *  taller version. */}
    </Wrapper>
  );
}

/**
 * Robinhood-style signed delta — `▲ $1,234.56 (1.23%)` in green for
 * positive, `▼ $1,234.56 (1.23%)` in red for negative. The triangle
 * conveys direction so we don't repeat a +/− on the dollar value.
 * Two lines: dollar amount on top (h6, prominent), pct in parens
 * inline next to it; falls back to a single line on tighter rows.
 *
 * Used in BotsSimpleView for both the Today/This-week left slot and
 * the Total return right slot.
 */
function DeltaValueRH({
  positive,
  dollar,
  fraction,
  align = "left"
}: {
  positive: boolean;
  dollar: number;
  fraction: number;
  align?: "left" | "right";
}) {
  const triangle = positive ? "▲" : "▼";
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={0.5}
      sx={{
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        fontFeatureSettings: '"tnum" 1',
        flexWrap: "nowrap"
      }}
    >
      <Typography
        component="span"
        variant="body1"
        sx={(t) => ({
          color: positive ? t.palette.success.main : t.palette.error.main,
          fontWeight: 700,
          lineHeight: 1.2,
          flexShrink: 0
        })}
      >
        {triangle}
      </Typography>
      <Typography
        component="span"
        variant="body1"
        sx={(t) => ({
          color: positive ? t.palette.success.main : t.palette.error.main,
          fontWeight: 700,
          fontFeatureSettings: '"tnum" 1',
          lineHeight: 1.2,
          whiteSpace: "nowrap"
        })}
      >
        {formatUSDSmart(Math.abs(dollar))}
      </Typography>
      <Typography
        component="span"
        variant="body1"
        sx={(t) => ({
          color: positive ? t.palette.success.main : t.palette.error.main,
          opacity: 0.85,
          fontWeight: 500,
          fontFeatureSettings: '"tnum" 1',
          lineHeight: 1.2,
          whiteSpace: "nowrap"
        })}
      >
        ({Math.abs(fraction * 100).toFixed(2)}%)
      </Typography>
    </Stack>
  );
}

/**
 * WHB-native bot mark — line-art robot icon, no background fill.
 * Sergey 2026-04-26: the previous coloured-square wrapper added
 * visual weight that competed with the ticker logos elsewhere on the
 * page. The outlined `SmartToy` glyph mirrors the icon shape WHB
 * uses next to bot names in its native UI; we render it in
 * `text.primary` so it inherits the row's foreground without a
 * coloured cell. PAUSED bots dim the icon to text.disabled.
 */
function BotIcon({
  status
}: {
  status: Bot["status"];
}) {
  const muted = status === "PAUSED";
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: muted ? "text.disabled" : "text.primary"
      }}
    >
      <SmartToyOutlined sx={{ fontSize: 28 }} />
    </Box>
  );
}

// ---- Small format helpers -------------------------------------------------

/**
 * Format a dollar amount, dropping cents whenever the absolute value
 * exceeds $999.99 (Sergey 2026-04-26). Keeps row widths tight on
 * larger numbers without sacrificing precision on small balances /
 * deltas. Examples:
 *
 *    $0.00       → "$0.00"
 *    $99.99      → "$99.99"
 *    $999.99     → "$999.99"
 *    $1,000.00   → "$1,000"
 *    $1,234.56   → "$1,235"
 *    $104,445.63 → "$104,446"
 */
function formatUSDSmart(n: number): string {
  if (Math.abs(n) > 999.99) {
    return formatUSD(n, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }
  return formatUSD(n);
}

/** Format a yyyy-mm-dd archived-at date as a compact "MMM D" label
 *  for the row's Archived chip. Local-time parsing keeps the
 *  displayed day on the user's calendar regardless of timezone. */
function formatArchivedDate(iso: string): string {
  if (!iso || iso.length < 10) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
